import React, { useEffect, useState } from 'react'
import T from 'prop-types'
import { Box, Button, Link as MuiLink, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'

import InputLabel from 'components/atoms/InputLabel'
import {
  BLIND_TYPE_NEGATIVE,
  BLIND_TYPE_POSITIVE,
  hasRemainingBlindOffersForType,
  normalizeBlindOffersRemaining,
  resolveBlindTypeFromCar,
  resolveBlindTypeFromPrices,
} from 'components/organisms/CarsToBuy/blindOffersLib'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'
const BLIND_PRICE_INCREMENT = 100
const REMOVE_BUTTON_ERROR_COLOR = 'error.main'
const STEP_PRECISION_EPSILON = 0.00001

const hasValue = (value) =>
  value !== null && value !== undefined && value !== ''

const parseOptionalNumber = (value) => {
  if (!hasValue(value)) {
    return null
  }

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : null
}

const isValidStep = (value) => {
  const remainder = value % BLIND_PRICE_INCREMENT
  return (
    Math.abs(remainder) < STEP_PRECISION_EPSILON ||
    Math.abs(Math.abs(remainder) - BLIND_PRICE_INCREMENT) <
      STEP_PRECISION_EPSILON
  )
}

const getBlindValidation = ({
  canEditBlindPrice,
  localValue,
  currentBlindPrice,
  groupPrice,
  reservePrice,
  isRemoveAction,
  allowBlindPositiveOffers,
}) => {
  if (!canEditBlindPrice || !hasValue(localValue) || isRemoveAction) {
    return {
      requiredBlindPrice: null,
      isIncrementInvalid: false,
      hasMinimumPriceError: false,
      isBlindPositiveDisabled: false,
      hasMinimumLimitError: false,
    }
  }

  const parsedLocalValue = parseOptionalNumber(localValue)
  const parsedGroupPrice = parseOptionalNumber(groupPrice)
  const parsedReservePrice = parseOptionalNumber(reservePrice)

  if (parsedLocalValue === null || parsedGroupPrice === null) {
    return {
      requiredBlindPrice: null,
      isIncrementInvalid: true,
      hasMinimumPriceError: false,
      isBlindPositiveDisabled: false,
      hasMinimumLimitError: false,
    }
  }

  if (parsedLocalValue === parsedGroupPrice) {
    return {
      requiredBlindPrice: null,
      isIncrementInvalid: true,
      hasMinimumPriceError: false,
      isBlindPositiveDisabled: false,
      hasMinimumLimitError: false,
    }
  }

  if (parsedLocalValue > parsedGroupPrice) {
    if (!allowBlindPositiveOffers) {
      return {
        requiredBlindPrice: null,
        isIncrementInvalid: true,
        hasMinimumPriceError: false,
        isBlindPositiveDisabled: true,
      }
    }

    const parsedCurrentBlindPrice = parseOptionalNumber(currentBlindPrice)
    const isCurrentBlindPositive =
      parsedCurrentBlindPrice !== null &&
      parsedCurrentBlindPrice > parsedGroupPrice
    const positiveBasePrice = isCurrentBlindPositive
      ? parsedCurrentBlindPrice
      : parsedGroupPrice
    const requiredBlindPrice = positiveBasePrice + BLIND_PRICE_INCREMENT
    const isBelowMinimum = parsedLocalValue < requiredBlindPrice
    const positiveDelta = parsedLocalValue - parsedGroupPrice
    const isInvalidPositiveStep = !isValidStep(positiveDelta)

    return {
      requiredBlindPrice,
      isIncrementInvalid: isBelowMinimum || isInvalidPositiveStep,
      hasMinimumPriceError: isBelowMinimum,
      isBlindPositiveDisabled: false,
      hasMinimumLimitError: false,
    }
  }

  const requiredBlindPrice = parsedReservePrice
  const negativeDelta = parsedGroupPrice - parsedLocalValue
  const isBelowFloor =
    requiredBlindPrice !== null && parsedLocalValue < requiredBlindPrice
  const isInvalidNegativeStep = !isValidStep(negativeDelta)

  return {
    requiredBlindPrice,
    isIncrementInvalid: isBelowFloor || isInvalidNegativeStep,
    hasMinimumPriceError: false,
    isBlindPositiveDisabled: false,
    hasMinimumLimitError: isBelowFloor,
  }
}

const getIncrementErrorMessage = ({
  t,
  isIncrementInvalid,
  hasMinimumPriceError,
  hasMinimumLimitError,
  requiredBlindPrice,
  isBlindPositiveDisabled,
}) => {
  if (!isIncrementInvalid) {
    return ''
  }

  if (isBlindPositiveDisabled) {
    return t('carsToBuy.blind.positiveDisabledInRepair')
  }

  if (hasMinimumLimitError) {
    return t('carsToBuy.blind.minimumLimitNotReached')
  }

  if (hasMinimumPriceError && requiredBlindPrice !== null) {
    return t('carsToBuy.blind.minimumOffer', {
      amount: new Intl.NumberFormat('it-IT').format(requiredBlindPrice),
    })
  }

  return t('carsToBuy.blind.minimumInput')
}

const resolveBlindPriceLabel = ({
  t,
  localValue,
  currentBlindPrice,
  groupPrice,
  blindType,
  allowBlindPositiveOffers,
}) => {
  if (blindType === BLIND_TYPE_POSITIVE && allowBlindPositiveOffers) {
    return t('carsToBuy.blind.priceLabelPositive')
  }
  if (blindType === BLIND_TYPE_NEGATIVE) {
    return t('carsToBuy.blind.priceLabelNegative')
  }

  const parsedGroupPrice = parseOptionalNumber(groupPrice)
  const parsedLocalValue = parseOptionalNumber(localValue)
  const parsedCurrentBlindPrice = parseOptionalNumber(currentBlindPrice)
  const parsedEffectiveValue =
    parsedLocalValue !== null ? parsedLocalValue : parsedCurrentBlindPrice

  if (parsedEffectiveValue === null || parsedGroupPrice === null) {
    return t('carsToBuy.blind.priceLabel')
  }

  if (parsedEffectiveValue > parsedGroupPrice && allowBlindPositiveOffers) {
    return t('carsToBuy.blind.priceLabelPositive')
  }
  if (parsedEffectiveValue < parsedGroupPrice) {
    return t('carsToBuy.blind.priceLabelNegative')
  }

  return t('carsToBuy.blind.priceLabel')
}

const getConfirmPayload = ({ currentBlindPrice, localValue }) =>
  currentBlindPrice !== Number(localValue) ? localValue || null : null

const getRemoveButtonStyle = ({ isDetails, isRemoveAction }) => ({
  marginLeft: (theme) => (isDetails ? undefined : theme.spacing(2)),
  ...(isRemoveAction
    ? {
        backgroundColor: 'common.white',
        color: REMOVE_BUTTON_ERROR_COLOR,
        borderColor: REMOVE_BUTTON_ERROR_COLOR,
        '&:hover': {
          backgroundColor: 'common.white',
          borderColor: REMOVE_BUTTON_ERROR_COLOR,
        },
      }
    : {}),
})

const CarBlindPriceDetailsLink = ({ isDetails, t, handleShowDetails }) => {
  if (isDetails) {
    return null
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
      <MuiLink
        color="primary"
        component={Box}
        onClick={handleShowDetails}
        variant="body2"
        sx={{ cursor: 'pointer' }}
      >
        <strong>{t('carsToBuy.details')}</strong>
      </MuiLink>
    </Box>
  )
}

const CarBlindPriceInput = ({
  isDetails,
  t,
  car,
  localValue,
  shortIncrementErrorMessage,
  incrementErrorMessage,
  repairHint,
  canEditBlindPrice,
  handleChange,
  handleConfirm,
  blindPriceLabel,
}) => (
  <div>
    {isDetails && (
      <InputLabel
        label={blindPriceLabel}
        inputId={`${car.plate}.blindPrice.input`}
        sx={{ color: 'text.primary' }}
      />
    )}
    <TextField
      id={`${car.plate}.blindPrice.input`}
      value={localValue ?? ''}
      label={
        !isDetails ? shortIncrementErrorMessage || blindPriceLabel : undefined
      }
      placeholder={isDetails ? t('carsToBuy.reprice.price') : undefined}
      type="number"
      size="small"
      variant={isDetails ? 'outlined' : undefined}
      fullWidth={isDetails}
      disabled={!canEditBlindPrice}
      inputProps={{ step: BLIND_PRICE_INCREMENT }}
      error={isDetails ? !!incrementErrorMessage : !!shortIncrementErrorMessage}
      helperText={
        isDetails ? incrementErrorMessage || repairHint || undefined : undefined
      }
      onChange={handleChange}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          event.stopPropagation()
          handleConfirm(event)
        }
      }}
    />
  </div>
)

const CarBlindPriceConfirmButton = ({
  showConfirmButton,
  isRemoveAction,
  isDisabled,
  handleConfirm,
  isDetails,
  t,
  currentBlindPrice,
  localValue,
}) => {
  if (!showConfirmButton) {
    return null
  }

  return (
    <Button
      type="button"
      color={isRemoveAction ? 'error' : 'primary'}
      variant={isRemoveAction ? 'outlined' : 'contained'}
      disabled={isDisabled}
      onClick={handleConfirm}
      sx={getRemoveButtonStyle({ isDetails, isRemoveAction })}
    >
      {t(
        `carsToBuy.blind.${
          currentBlindPrice !== Number(localValue)
            ? 'confirmPrice'
            : 'removePrice'
        }`
      )}
    </Button>
  )
}

const CarBlindPriceView = ({
  isDetails,
  t,
  handleShowDetails,
  car,
  localValue,
  shortIncrementErrorMessage,
  incrementErrorMessage,
  repairHint,
  canEditBlindPrice,
  handleChange,
  handleConfirm,
  showConfirmButton,
  isRemoveAction,
  isDisabled,
  currentBlindPrice,
  blindPriceLabel,
  ...others
}) => (
  <Stack
    direction={isDetails ? undefined : 'row'}
    justifyContent="space-between"
    alignItems={isDetails ? undefined : 'center'}
    mt={2}
    data-testid="car-blind-price"
    {...others}
  >
    <CarBlindPriceDetailsLink
      isDetails={isDetails}
      t={t}
      handleShowDetails={handleShowDetails}
    />
    <Stack
      direction={isDetails ? undefined : 'row'}
      justifyContent={isDetails ? undefined : 'flex-end'}
      mt={2}
    >
      <CarBlindPriceInput
        isDetails={isDetails}
        t={t}
        car={car}
        localValue={localValue}
        shortIncrementErrorMessage={shortIncrementErrorMessage}
        incrementErrorMessage={incrementErrorMessage}
        repairHint={repairHint}
        canEditBlindPrice={canEditBlindPrice}
        handleChange={handleChange}
        handleConfirm={handleConfirm}
        blindPriceLabel={blindPriceLabel}
      />
      {isDetails && <Box pb={2} pr={2} />}
      <CarBlindPriceConfirmButton
        showConfirmButton={showConfirmButton}
        isRemoveAction={isRemoveAction}
        isDisabled={isDisabled}
        handleConfirm={handleConfirm}
        isDetails={isDetails}
        t={t}
        currentBlindPrice={currentBlindPrice}
        localValue={localValue}
      />
    </Stack>
  </Stack>
)

export default function CarBlindPrice({
  variant = VARIANT_LIST,
  car,
  handleConfirmBlindPrice = () => {},
  handleShowDetails,
  readOnly = false,
  enabled = true,
  blindOffersRemaining,
  showConfirmButton = true,
  allowBlindPositiveOffers = true,
  showRepairHint = false,
  ...others
}) {
  const { t } = useTranslation()

  const currentBlindPrice = car.blindPrice ?? car.reprice
  const groupPrice = car.price
  const reservePrice = car.reservePrice

  const [localValue, setLocalValue] = useState('')

  useEffect(() => {
    setLocalValue(currentBlindPrice ?? '')
  }, [currentBlindPrice])

  const handleChange = (e) => {
    setLocalValue(e.target.value)
  }

  const handleConfirm = (event) => {
    if (event?.preventDefault) {
      event.preventDefault()
    }
    if (event?.stopPropagation) {
      event.stopPropagation()
    }
    if (isDisabled) {
      return
    }
    handleConfirmBlindPrice(
      car.plate,
      getConfirmPayload({ currentBlindPrice, localValue })
    )
  }

  const isDetails = variant === VARIANT_DETAILS

  const hasExistingBlindPrice = hasValue(currentBlindPrice)
  const hasTypedValue = hasValue(localValue)
  const normalizedBlindOffersRemaining =
    normalizeBlindOffersRemaining(blindOffersRemaining)
  const nextBlindType = resolveBlindTypeFromPrices({
    blindPrice: localValue,
    groupPrice,
  })
  const currentBlindType = resolveBlindTypeFromCar({
    ...car,
    blindPrice: currentBlindPrice,
    price: groupPrice,
  })
  const canEditBlindPrice = enabled && readOnly !== true

  const parsedLocalValue = parseOptionalNumber(localValue)
  const parsedCurrentBlindPrice = parseOptionalNumber(currentBlindPrice)
  const isRemoveAction =
    hasExistingBlindPrice &&
    parsedLocalValue !== null &&
    parsedCurrentBlindPrice !== null &&
    parsedLocalValue === parsedCurrentBlindPrice
  const {
    requiredBlindPrice,
    isIncrementInvalid,
    hasMinimumPriceError,
    isBlindPositiveDisabled,
    hasMinimumLimitError,
  } = getBlindValidation({
    canEditBlindPrice,
    localValue,
    currentBlindPrice,
    groupPrice,
    reservePrice,
    isRemoveAction,
    allowBlindPositiveOffers,
  })
  const incrementErrorMessage = getIncrementErrorMessage({
    t,
    isIncrementInvalid,
    hasMinimumPriceError,
    hasMinimumLimitError,
    requiredBlindPrice,
    isBlindPositiveDisabled,
  })
  const shortIncrementErrorMessage =
    isBlindPositiveDisabled || hasMinimumLimitError
      ? incrementErrorMessage
      : isIncrementInvalid
      ? t('carsToBuy.blind.minimumInput')
      : ''
  const blindPriceLabel = resolveBlindPriceLabel({
    t,
    localValue,
    currentBlindPrice,
    groupPrice,
    blindType: car?.blindType,
    allowBlindPositiveOffers,
  })
  const repairHint =
    showRepairHint && !allowBlindPositiveOffers
      ? t('carsToBuy.repair.blindPriceHint')
      : ''
  const nextPayload = getConfirmPayload({ currentBlindPrice, localValue })
  const isUpdatingBlindPrice = hasValue(nextPayload)
  const requiresTargetTypeSlot =
    isUpdatingBlindPrice &&
    nextBlindType !== null &&
    (!hasExistingBlindPrice || nextBlindType !== currentBlindType)
  const hasRemainingSlotForTargetType =
    !requiresTargetTypeSlot ||
    ((nextBlindType !== BLIND_TYPE_POSITIVE || allowBlindPositiveOffers) &&
      hasRemainingBlindOffersForType(
        normalizedBlindOffersRemaining,
        nextBlindType
      ))

  const isDisabled =
    !enabled ||
    (!hasTypedValue && !hasExistingBlindPrice) ||
    readOnly ||
    !hasRemainingSlotForTargetType ||
    isIncrementInvalid

  return (
    <CarBlindPriceView
      isDetails={isDetails}
      t={t}
      handleShowDetails={handleShowDetails}
      car={car}
      localValue={localValue}
      shortIncrementErrorMessage={shortIncrementErrorMessage}
      incrementErrorMessage={incrementErrorMessage}
      repairHint={repairHint}
      canEditBlindPrice={canEditBlindPrice}
      handleChange={handleChange}
      handleConfirm={handleConfirm}
      showConfirmButton={showConfirmButton}
      isRemoveAction={isRemoveAction}
      isDisabled={isDisabled}
      currentBlindPrice={currentBlindPrice}
      blindPriceLabel={blindPriceLabel}
      {...others}
    />
  )
}

CarBlindPrice.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  car: T.object.isRequired,
  handleConfirmBlindPrice: T.func,
  handleShowDetails: T.func,
  readOnly: T.bool,
  enabled: T.bool,
  blindOffersRemaining: T.oneOfType([
    T.number,
    T.shape({
      positive: T.number,
      negative: T.number,
    }),
  ]),
  showConfirmButton: T.bool,
  allowBlindPositiveOffers: T.bool,
  showRepairHint: T.bool,
}
